// server/scripts/modules/settings-drawer.mjs
import settings from './settings-store.mjs';

const FADE_DELAY = 3000;

let fadeTimer = null;
let drawerOpen = false;

const scheduleGearFade = () => {
	clearTimeout(fadeTimer);
	fadeTimer = setTimeout(() => {
		if (!drawerOpen) {
			document.getElementById('settings-gear-btn')?.classList.add('faded');
		}
	}, FADE_DELAY);
};

const cancelGearFade = () => {
	clearTimeout(fadeTimer);
	document.getElementById('settings-gear-btn')?.classList.remove('faded');
};

const openDrawer = () => {
	drawerOpen = true;
	cancelGearFade();
	document.getElementById('settings-drawer')?.classList.add('open');
	document.getElementById('settings-backdrop')?.classList.add('open');
};

const closeDrawer = () => {
	drawerOpen = false;
	document.getElementById('settings-drawer')?.classList.remove('open');
	document.getElementById('settings-backdrop')?.classList.remove('open');
	scheduleGearFade();
};

const buildToggleRow = (label, key) => {
	const row = document.createElement('div');
	row.className = 'sd-row';

	const lbl = document.createElement('label');
	lbl.textContent = label;

	const wrapper = document.createElement('div');
	wrapper.className = 'sd-toggle';

	const checkbox = document.createElement('input');
	checkbox.type = 'checkbox';
	checkbox.checked = settings.get(key);
	checkbox.addEventListener('change', () => settings.set(key, checkbox.checked));

	settings.on(key, (val) => { checkbox.checked = val; });

	const track = document.createElement('div');
	track.className = 'sd-toggle-track';
	track.addEventListener('click', () => {
		checkbox.checked = !checkbox.checked;
		checkbox.dispatchEvent(new Event('change'));
	});

	wrapper.append(checkbox, track);
	row.append(lbl, wrapper);
	return row;
};

const buildSelectRow = (label, key, id) => {
	const row = document.createElement('div');
	row.className = 'sd-row';
	if (id) row.id = id;

	const lbl = document.createElement('label');
	lbl.textContent = label;

	const select = document.createElement('select');
	select.className = 'sd-select';

	const def = settings.schema[key];
	def.values.forEach(([val, text]) => {
		const opt = document.createElement('option');
		opt.value = typeof val === 'number' ? val.toFixed(2) : val;
		opt.textContent = text;
		opt.selected = settings.get(key) === val || settings.get(key)?.toFixed?.(2) === opt.value;
		select.append(opt);
	});

	select.addEventListener('change', () => {
		const num = parseFloat(select.value);
		settings.set(key, Number.isNaN(num) ? select.value : num);
	});

	settings.on(key, (val) => {
		select.value = typeof val === 'number' ? val.toFixed(2) : val;
	});

	row.append(lbl, select);
	return row;
};

const buildNavButton = (label, targetId) => {
	const btn = document.createElement('button');
	btn.className = 'sd-nav-btn';
	btn.textContent = label;
	btn.addEventListener('click', () => {
		document.getElementById(targetId)?.click();
		closeDrawer();
	});
	return btn;
};

const buildLocationButton = () => {
	const btn = document.createElement('button');
	btn.className = 'sd-location-btn';
	btn.textContent = '📍 Change Location…';
	btn.addEventListener('click', () => {
		closeDrawer();
		const divQuery = document.getElementById('divQuery');
		if (divQuery) {
			divQuery.classList.add('visible');
			divQuery.style.display = 'flex';
			document.getElementById('txtLocation')?.focus();
		}
	});
	return btn;
};

const buildDrawer = () => {
	const drawer = document.createElement('div');
	drawer.id = 'settings-drawer';

	// Header
	const header = document.createElement('div');
	header.className = 'sd-header';
	const title = document.createElement('span');
	title.className = 'sd-title';
	title.textContent = 'SETTINGS';
	const closeBtn = document.createElement('button');
	closeBtn.className = 'sd-close';
	closeBtn.textContent = '✕';
	closeBtn.addEventListener('click', closeDrawer);
	header.append(title, closeBtn);

	// Body
	const body = document.createElement('div');
	body.className = 'sd-body';

	// Display section
	const displaySection = document.createElement('div');
	displaySection.className = 'sd-section';
	const displayLabel = document.createElement('div');
	displayLabel.className = 'sd-section-label';
	displayLabel.textContent = 'DISPLAY';
	const scanLinesRow = buildToggleRow('Scan Lines', 'scanLines');
	const scanLineModeRow = buildSelectRow('Scan Line Style', 'scanLineMode', 'sd-scanLineMode-row');
	const speedRow = buildSelectRow('Speed', 'speed');
	const unitsRow = buildSelectRow('Units', 'units');

	// Show/hide scanLineMode row based on scanLines state
	const updateScanLineModeVisibility = (val) => {
		scanLineModeRow.style.display = val ? '' : 'none';
	};
	updateScanLineModeVisibility(settings.get('scanLines'));
	settings.on('scanLines', updateScanLineModeVisibility);

	displaySection.append(displayLabel, scanLinesRow, scanLineModeRow, speedRow, unitsRow);

	// Data section
	const dataDivider = document.createElement('div');
	dataDivider.className = 'sd-divider';
	const dataSection = document.createElement('div');
	dataSection.className = 'sd-section';
	const dataLabel = document.createElement('div');
	dataLabel.className = 'sd-section-label';
	dataLabel.textContent = 'DATA';
	const refreshRow = buildSelectRow('Refresh', 'refreshTime');
	dataSection.append(dataLabel, refreshRow);

	// Audio section
	const audioDivider = document.createElement('div');
	audioDivider.className = 'sd-divider';
	const audioSection = document.createElement('div');
	audioSection.className = 'sd-section';
	const audioLabel = document.createElement('div');
	audioLabel.className = 'sd-section-label';
	audioLabel.textContent = 'AUDIO';
	const stingersRow = buildToggleRow('Display Stingers', 'stingersEnabled');
	audioSection.append(audioLabel, stingersRow);

	// Location section
	const locationDivider = document.createElement('div');
	locationDivider.className = 'sd-divider';
	const locationSection = document.createElement('div');
	locationSection.className = 'sd-section';
	const locationLabel = document.createElement('div');
	locationLabel.className = 'sd-section-label';
	locationLabel.textContent = 'LOCATION';
	const locationBtn = buildLocationButton();
	locationSection.append(locationLabel, locationBtn);

	// Navigation section
	const navDivider = document.createElement('div');
	navDivider.className = 'sd-divider';
	const navSection = document.createElement('div');
	navSection.className = 'sd-section';
	const navLabel = document.createElement('div');
	navLabel.className = 'sd-section-label';
	navLabel.textContent = 'NAVIGATION';
	const navGrid = document.createElement('div');
	navGrid.className = 'sd-nav-grid';
	navGrid.append(
		buildNavButton('⏮ Prev', 'NavigatePrevious'),
		buildNavButton('Next ⏭', 'NavigateNext'),
		buildNavButton('⏸ Pause', 'NavigatePlay'),
		buildNavButton('↺ Refresh', 'NavigateRefresh'),
	);
	navSection.append(navLabel, navGrid);

	body.append(
		displaySection,
		dataDivider,
		dataSection,
		audioDivider,
		audioSection,
		locationDivider,
		locationSection,
		navDivider,
		navSection,
	);

	drawer.append(header, body);
	return drawer;
};

const init = () => {
	// Gear button
	const gear = document.createElement('div');
	gear.id = 'settings-gear-btn';
	gear.textContent = '⚙';
	gear.title = 'Settings';
	gear.addEventListener('click', openDrawer);

	// Backdrop (click outside to close)
	const backdrop = document.createElement('div');
	backdrop.id = 'settings-backdrop';
	backdrop.addEventListener('click', closeDrawer);

	const drawer = buildDrawer();

	document.body.append(gear, backdrop, drawer);

	// Hide #divQuery when location form submits or resets
	const hideDivQuery = () => {
		const divQuery = document.getElementById('divQuery');
		if (divQuery) {
			divQuery.classList.remove('visible');
			divQuery.style.display = 'none';
		}
	};
	document.getElementById('btnGetLatLng')?.addEventListener('click', hideDivQuery);
	document.getElementById('btnClearQuery')?.addEventListener('click', hideDivQuery);

	// Start gear fade timer
	scheduleGearFade();

	// Reset fade timer on any user interaction
	const resetFade = () => {
		if (!drawerOpen) {
			cancelGearFade();
			scheduleGearFade();
		}
	};
	document.addEventListener('mousemove', resetFade);
	document.addEventListener('touchstart', resetFade, { passive: true });
};

document.addEventListener('DOMContentLoaded', init);
